import { Directive, ElementRef, HostListener, inject, input } from '@angular/core';
import { RegexValidators } from '@pc/shared';
import { ARROW_KEYS, DECIMAL_POINT, SPECIAL_KEYS } from './input-special-keys';

@Directive({
  standalone: true,
  selector: 'input:[pcNumbersOnly]'
})
export class OnlyNumbersDirective {
  public appNumbersOnly = input(false);
  public allowDecimal = input(false);
  public isAmountInput = input(false);

  private readonly MAX_DECIMAL_DIGITS = 2;
  private el = inject(ElementRef);

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    const isAlt = event.altKey;
    const isShift = event.shiftKey;

    if (!this.isInputValid(key, isCtrlOrCmd, isAlt, isShift)) {
      event.preventDefault();
      return;
    }

    if (this.appNumbersOnly() && this.allowDecimal() && this.isAmountInput() && RegexValidators.NUMBERS.test(key)) {
      const input = this.el.nativeElement;
      const currentValue = input.value;
      const cursorStart = input.selectionStart ?? 0;
      const cursorEnd = input.selectionEnd ?? 0;
      const simulatedValue = currentValue.slice(0, cursorStart) + key + currentValue.slice(cursorEnd);

      const [integerPart] = simulatedValue.split(DECIMAL_POINT);
      if (/^0\d+/.test(integerPart)) {
        event.preventDefault();
        return;
      }

      const decimalIndex = simulatedValue.indexOf(DECIMAL_POINT);
      if (decimalIndex >= 0) {
        const decimalPart = simulatedValue.substring(decimalIndex + 1);
        if (decimalPart.length > this.MAX_DECIMAL_DIGITS) {
          event.preventDefault();
        }
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(): void {
    setTimeout(() => {
      if (this.appNumbersOnly() && this.allowDecimal() && this.isAmountInput()) {
        const input = this.el.nativeElement;
        const value = input.value;
        if (!value) return;

        const [integerPartRaw, decimalPartRaw = ''] = value.split('.');
        const sanitizedInteger = integerPartRaw.replace(/^0+/, '') || '0';
        const truncatedDecimal = decimalPartRaw.slice(0, this.MAX_DECIMAL_DIGITS);

        input.value = truncatedDecimal ? `${sanitizedInteger}.${truncatedDecimal}` : sanitizedInteger;
      }
    });
  }

  private isInputValid(key: string, isCtrlOrCmd: boolean, isAlt: boolean, isShift: boolean): boolean {
    if (!this.appNumbersOnly()) {
      return true;
    }

    // Allow numeric input
    if (RegexValidators.NUMBERS.test(key)) {
      return true;
    }

    // Allow Enter key
    if (key === 'Enter') {
      return true;
    }

    // Allow special keys
    if (SPECIAL_KEYS.includes(key)) {
      return true;
    }

    if (key === DECIMAL_POINT && this.allowDecimal()) {
      const input = this.el.nativeElement;
      const currentValue: string = input.value;
      const cursorPosition = input.selectionStart ?? 0;

      const currentInput: string = this.el.nativeElement.value;

      if (currentInput.includes(DECIMAL_POINT)) {
        return false;
      }

      return !(currentValue.length === 0 || cursorPosition === 0);
    }

    // Allow combinations like Ctrl+C, Ctrl+V, etc.
    if (isCtrlOrCmd) {
      return true;
    }

    // Allow Shift + Arrow keys or Alt + Arrow keys
    if (ARROW_KEYS.includes(key) && (isShift || isAlt)) {
      return true;
    }

    return false;
  }
}
